import { useEffect, useMemo } from "react"

import { Navigate, Route, Routes, useSearchParams } from "react-router-dom"

import { BrowserStorageType } from "@front-shared/config/general.config.ts"
import { NirvanaFontsElement } from "@front-shared/elements/nirvanaFonts.element.tsx"
import { OfflineElement } from "@front-shared/elements/offline.element.tsx"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper.ts"
import { useIsOnline } from "@front-shared/hooks/isOnline.hook.ts"

import { HeaderComponent } from "../components/general/header.component"
import { NirvanaBackgroundComponent } from "../components/general/nirvanaBackground.component"
import { NotFoundComponent } from "../components/general/notFound.component"
import { PracticeAcceptInvitationComponent } from "../components/general/practiceAcceptInvitation.component"
import { PracticeMissingBAALockComponent } from "../components/general/practiceMissingBAALock.component"
import { PracticeRoleMissingLockComponent } from "../components/general/practiceRoleMissingLock.component"
import { SupportedPayersListsComponent } from "../components/general/supportedPayersList.component"
import { GENERAL_CONFIG } from "../config/general.config"
import { ROUTES_CONFIG } from "../config/routes.config"
import { AuthHelper } from "../helpers/auth.helper"
import { useCheckAppVersion } from "../hooks/checkAppVersion.hook"
import { useFlashMessage } from "../hooks/flashMessage.hook"
import { useAppDispatch } from "../store/appDispatch.hook"
import { useAppSelector } from "../store/selectors/app.selector"
import { authSelector } from "../store/selectors/auth.selector"
import { runtimeSelector } from "../store/selectors/runtime.selector"
import { userDetailsSelector } from "../store/selectors/userDetails.selector"
import { authCheckInviteToken } from "../store/thunks/auth.thunks"
import { runtimeCheckUserIsLoggedIn } from "../store/thunks/runtime.thunks"
import { AppContainer } from "./app.container"
import { AuthContainer } from "./auth.container"

// import { DowntimeComponent } from "components/general/downtime.component"

export const RouterContainer = () => {
    const dispatch = useAppDispatch()

    useCheckAppVersion()
    useFlashMessage()

    const isOnline = useIsOnline()

    const [searchParams] = useSearchParams()

    const authState = useAppSelector(authSelector)
    const userDetailsState = useAppSelector(userDetailsSelector)
    const { isLoggedIn } = useAppSelector(runtimeSelector)

    useEffect(() => {
        if (isLoggedIn === null) {
            dispatch(runtimeCheckUserIsLoggedIn())
        }
    }, [isLoggedIn])

    // On mount see if there's invite token
    useEffect(() => {
        const inviteToken =
            searchParams.get(GENERAL_CONFIG.urlSearchParamsKeys.inviteToken) ||
            BrowserStorageHelper.get(
                GENERAL_CONFIG.browserStorageKeys.inviteToken,
                undefined,
                BrowserStorageType.sessionStorage,
            )

        if (!inviteToken) {
            return
        }

        dispatch(
            authCheckInviteToken({
                payload: inviteToken,

                onSuccess: () => {
                    // Set it to browser storage so it can be used across app
                    // Either before login or after login
                    BrowserStorageHelper.set(
                        GENERAL_CONFIG.browserStorageKeys.inviteToken,
                        inviteToken,
                        BrowserStorageType.sessionStorage,
                    )
                },
            }),
        )
    }, [isLoggedIn])

    const showMissingBAALock = useMemo(() => {
        return (
            userDetailsState.selectedPracticeRole &&
            !userDetailsState.selectedPracticeRole.practice?.hasSignedBAA &&
            AuthHelper.getEagleEyeViewAsData() === null
        )
    }, [userDetailsState.selectedPracticeRole?.practice?.id])

    return isLoggedIn !== null ? (
        <div>
            <NirvanaFontsElement />

            <HeaderComponent />

            {/*<DowntimeComponent />*/}

            {isOnline ? (
                <Routes>
                    <Route
                        path={`${ROUTES_CONFIG.authUrl}/*`}
                        element={<AuthContainer />}
                    />

                    <Route
                        path={ROUTES_CONFIG.supportedPayersStandardUrl}
                        element={<SupportedPayersListsComponent />}
                    />

                    <Route
                        path={ROUTES_CONFIG.supportedPayersDiscoverUrl}
                        element={
                            <SupportedPayersListsComponent
                                isDiscoverSupported
                            />
                        }
                    />

                    <Route
                        path={ROUTES_CONFIG.supportedPayersAgenticUrl}
                        element={
                            <SupportedPayersListsComponent
                                isAgenticPortalSupported
                                isAgenticPhoneSupported
                            />
                        }
                    />

                    <Route path="/*" element={<AppContainer />} />

                    <Route path="*" element={<NotFoundComponent />} />

                    <Route
                        path="/"
                        element={
                            <Navigate
                                replace
                                to={
                                    isLoggedIn
                                        ? ROUTES_CONFIG.baseApplicationUrl
                                        : ROUTES_CONFIG.loginUrl
                                }
                            />
                        }
                    />
                </Routes>
            ) : (
                <OfflineElement />
            )}

            <NirvanaBackgroundComponent />

            {isLoggedIn && authState.userInvitation ? (
                <PracticeAcceptInvitationComponent
                    invite={authState.userInvitation}
                />
            ) : userDetailsState.profile &&
              !userDetailsState.profile.practiceRoles.length ? (
                <PracticeRoleMissingLockComponent />
            ) : showMissingBAALock ? (
                <PracticeMissingBAALockComponent />
            ) : null}
        </div>
    ) : null
}
